import React, { useEffect, useState } from 'react';
import UserForm from './UserForm';
import '../../css/userList.css';
import apiservice from "../../shared/services/ApiService";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [filterRole, setFilterRole] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [roles, setRoles] = useState([]);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9);
    
    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await apiservice.get(`/api/roles`);
            setRoles(response.data);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };
    
    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await apiservice.get(`/api/usuarios`);
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error('Error al cargar usuarios');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleDelete = async (id, username) => {
        if (window.confirm(`¿Estás seguro que deseas eliminar a ${username}?`)) {
            try {
                await apiservice.delete(`/api/usuarios/${id}`);
                toast.success(`Usuario ${username} eliminado correctamente`);
                fetchUsers();
            } catch (error) {
                console.error("Error deleting user:", error);
                toast.error('Error al eliminar usuario');
            }
        }
    };
    
    const handleEdit = (user) => {
        setSelectedUser(user);
        setShowForm(true);
        setTimeout(() => {
            const formElement = document.querySelector('.user-form-container-modern');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };
    
    const handleSubmit = async (userData) => {
        setIsLoading(true);
        try {
            if (selectedUser) {
                await apiservice.put(`/api/usuarios/${selectedUser._id}`, userData);
                toast.success('Usuario actualizado correctamente');
            } else {
                await apiservice.post(`/api/usuarios`, userData);
                toast.success('Usuario creado correctamente');
            }
            setSelectedUser(null);
            setShowForm(false);
            fetchUsers();
        } catch (error) {
            console.error("Error saving user:", error);
            toast.error(selectedUser ? 'Error al actualizar usuario' : 'Error al crear usuario');
            setIsLoading(false);
        }
    };

    // Filtrado y ordenamiento
    let filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = filterRole === 'all' || 
            (user.role?._id === filterRole || user.role === filterRole);
        
        return matchesSearch && matchesRole;
    });

    // Ordenamiento
    filteredUsers = [...filteredUsers].sort((a, b) => {
        if (sortBy === 'name') {
            return a.username.localeCompare(b.username);
        } else if (sortBy === 'email') {
            return a.email.localeCompare(b.email);
        }
        return 0;
    });

    // Estadísticas
    const statistics = {
        total: users.length,
        byRole: roles.reduce((acc, role) => {
            acc[role._id] = users.filter(u => 
                u.role?._id === role._id || u.role === role._id
            ).length;
            return acc;
        }, {})
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    
    return (
        <div className="user-management-container">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="user-header-modern">
                <div className="header-content-user">
                    <div className="header-title-section-user">
                        <div className="title-icon-user">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="page-title-user">Gestión de Usuarios</h1>
                            <p className="page-subtitle-user">Administra los usuarios del sistema</p>
                        </div>
                    </div>
                    <div className="header-actions-user">
                        <button
                            className={`btn-primary-user ${showForm ? 'btn-danger-user' : ''}`}
                            onClick={() => {
                                setShowForm(!showForm);
                                if (showForm) {
                                    setSelectedUser(null);
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {showForm ? (
                                    <>
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </>
                                ) : (
                                    <>
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </>
                                )}
                            </svg>
                            {showForm ? 'Cancelar' : 'Nuevo Usuario'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="stats-section-user">
                <div className="stat-card-user total">
                    <div className="stat-icon-user">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <div className="stat-content-user">
                        <h3>{statistics.total}</h3>
                        <p>Total Usuarios</p>
                    </div>
                </div>

                {roles.slice(0, 3).map((role, index) => (
                    <div key={role._id} className={`stat-card-user role-${index + 1}`}>
                        <div className="stat-icon-user">
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div className="stat-content-user">
                            <h3>{statistics.byRole[role._id] || 0}</h3>
                            <p>{role.name}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Formulario */}
            {showForm && (
                <div className="form-wrapper-user">
                    <UserForm 
                        onSubmit={handleSubmit} 
                        user={selectedUser} 
                        onCancel={() => {
                            setSelectedUser(null);
                            setShowForm(false);
                        }}
                    />
                </div>
            )}

            {/* Lista de usuarios */}
            <div className="user-list-section-modern">
                {/* Controles */}
                <div className="list-controls-user">
                    <div className="controls-left-user">
                        <div className="search-box-user">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Buscar por nombre o email..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {searchTerm && (
                                <button 
                                    className="clear-search-btn-user"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="filter-group-user">
                            <select
                                className="filter-select-user"
                                value={filterRole}
                                onChange={(e) => {
                                    setFilterRole(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="all">Todos los roles</option>
                                {roles.map(role => (
                                    <option key={role._id} value={role._id}>{role.name}</option>
                                ))}
                            </select>

                            <select
                                className="filter-select-user"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="name">Ordenar por nombre</option>
                                <option value="email">Ordenar por email</option>
                            </select>
                        </div>
                    </div>

                    <div className="controls-right-user">
                        <div className="view-toggles-user">
                            <button
                                className={`view-btn-user ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Vista de cuadrícula"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="14" y="14" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                            </button>
                            <button
                                className={`view-btn-user ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                                title="Vista de lista"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="8" y1="6" x2="21" y2="6"/>
                                    <line x1="8" y1="12" x2="21" y2="12"/>
                                    <line x1="8" y1="18" x2="21" y2="18"/>
                                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Contenido */}
                {isLoading ? (
                    <div className="loading-state-user">
                        <div className="spinner-user"></div>
                        <p>Cargando usuarios...</p>
                    </div>
                ) : (
                    <>
                        {currentUsers.length > 0 ? (
                            <div className={`users-${viewMode}`}>
                                {currentUsers.map((user) => (
                                    <div key={user._id} className="user-card-modern">
                                        <div className="user-card-header">
                                            <div className="user-avatar-modern">
                                                {user.username.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="user-info-modern">
                                                <h3 className="user-name-modern">{user.username}</h3>
                                                <p className="user-email-modern">{user.email}</p>
                                            </div>
                                        </div>

                                        <div className="user-card-body">
                                            <div className="user-detail-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="12" cy="7" r="4"/>
                                                </svg>
                                                <span className="user-role-badge">
                                                    {user.role?.name || 'Sin rol'}
                                                </span>
                                            </div>
                                            <div className="user-detail-item user-brigades-row">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="9" cy="7" r="4"/>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                                <div className="user-brigades-tags">
                                                    {user.brigadas && user.brigadas.length > 0 ? (
                                                        user.brigadas.map(b => (
                                                            <span key={b._id || b} className="brigade-tag">
                                                                {b.name || 'Brigada'}
                                                            </span>
                                                        ))
                                                    ) : user.brigada?.name ? (
                                                        <span className="brigade-tag">{user.brigada.name}</span>
                                                    ) : (
                                                        <span className="user-brigade-text">Sin brigada</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="user-card-actions">
                                            <button
                                                className="action-btn-user edit"
                                                onClick={() => handleEdit(user)}
                                                title="Editar usuario"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="action-btn-user delete"
                                                onClick={() => handleDelete(user._id, user.username)}
                                                title="Eliminar usuario"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"/>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state-user">
                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                <h3>No hay usuarios</h3>
                                <p>
                                    {searchTerm || filterRole !== 'all'
                                        ? 'No se encontraron usuarios con los filtros aplicados'
                                        : 'No hay usuarios registrados. Crea uno nuevo para comenzar.'}
                                </p>
                                {(searchTerm || filterRole !== 'all') && (
                                    <button
                                        className="btn-clear-filters-user"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilterRole('all');
                                        }}
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="pagination-user">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="page-btn-user"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                </button>

                                <div className="page-numbers-user">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(num =>
                                            num === 1 ||
                                            num === totalPages ||
                                            (num >= currentPage - 1 && num <= currentPage + 1)
                                        )
                                        .map((number, index, array) => {
                                            if (index > 0 && array[index - 1] !== number - 1) {
                                                return (
                                                    <React.Fragment key={`ellipsis-${number}`}>
                                                        <span className="ellipsis-user">...</span>
                                                        <button
                                                            onClick={() => setCurrentPage(number)}
                                                            className={`page-btn-user ${currentPage === number ? 'active' : ''}`}
                                                        >
                                                            {number}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={number}
                                                    onClick={() => setCurrentPage(number)}
                                                    className={`page-btn-user ${currentPage === number ? 'active' : ''}`}
                                                >
                                                    {number}
                                                </button>
                                            );
                                        })
                                    }
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="page-btn-user"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"/>
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div className="pagination-info-user">
                            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredUsers.length)} de {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserList;